import { Atom, PrimitiveAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useReducer, useRef } from 'react';
import {
  AssetState,
  AssetUpdateType,
  LiveMapState,
  LiveStateOrFullUpdate,
  LiveStateUpdate,
} from '../util/Events/Messages';
import {
  AssetCoordinates,
  AssetCoordinatesDwell,
  AssetCoordinatesTriggered,
  AssetSensed,
  AssetSensedTriggered,
  ProjectlessAssetIdentifiers,
} from '../util/Events/schema';
import { uniqBy } from '../util/arrayUtils';
import { stringifyIdRecord } from '../util/stringUtils';
import { AssetEntity } from '../util/enums';
import { compareDesc, differenceInMinutes } from 'date-fns';
import { Notification } from '../util/Events/Messages';
import { LIVE_NOTIFICATIONS } from '../store/notifications';
import { ExtendedMap } from '../util/mapUtils';
import { assetTypeOfEntity } from '../Components/Map/commons';
import { CID, PID } from '../store/user';
import { createGeofenceEventUniqueIdentifier } from '../util/geofenceEventUtils';

/**
 * Safety-net upper bound on per-asset `recentLocations` to prevent unbounded
 * growth (the JIRA acceptance criterion calls for a high ceiling — "tens of
 * thousands of points"). At 1 coord/sec this allows ~14h of continuous Live
 * accumulation before the cap kicks in.
 */
export const MAX_RECENT_LOCATIONS = 50_000;
/** Safety-net upper bound on per-asset `recentFenceEvents`. */
export const MAX_RECENT_FENCE_EVENTS = 50_000;

const warnedRecentLocationsCapAssetKeys = new Set<string>();
const warnedRecentFenceEventsCapAssetKeys = new Set<string>();

/**
 * Test-only reset for the per-asset "cap reached" warning sentinels. Module
 * state would otherwise leak across test cases that share asset keys.
 */
export const __resetStreamSafetyNetWarningsForTest = () => {
  warnedRecentLocationsCapAssetKeys.clear();
  warnedRecentFenceEventsCapAssetKeys.clear();
};

const capRecentLocations = (
  recentLocations: AssetCoordinates[],
  assetId: Record<string, string | undefined>,
): AssetCoordinates[] => {
  if (recentLocations.length <= MAX_RECENT_LOCATIONS) return recentLocations;
  const key = stringifyIdRecord(assetId);
  if (!warnedRecentLocationsCapAssetKeys.has(key)) {
    warnedRecentLocationsCapAssetKeys.add(key);
    console.warn(
      `recentLocations for asset ${key} reached safety-net cap of ${MAX_RECENT_LOCATIONS}; older entries will be dropped`,
    );
  }
  return recentLocations.slice(0, MAX_RECENT_LOCATIONS);
};

const capRecentFenceEvents = <T>(
  recentFenceEvents: T[],
  assetId: Record<string, string | undefined>,
): T[] => {
  if (recentFenceEvents.length <= MAX_RECENT_FENCE_EVENTS) return recentFenceEvents;
  const key = stringifyIdRecord(assetId);
  if (!warnedRecentFenceEventsCapAssetKeys.has(key)) {
    warnedRecentFenceEventsCapAssetKeys.add(key);
    console.warn(
      `recentFenceEvents for asset ${key} reached safety-net cap of ${MAX_RECENT_FENCE_EVENTS}; older entries will be dropped`,
    );
  }
  return recentFenceEvents.slice(0, MAX_RECENT_FENCE_EVENTS);
};

export type StreamData = {
  state: LiveMapState;
  startDate: Date;
};

export const INITIAL_STATE: LiveMapState = {
  assets: new ExtendedMap<string, AssetState>(),
  lastUpdate: new Date(),
  startDate: new Date(),
  notifications: [],
};

const emptyAssetState = (ids: ProjectlessAssetIdentifiers): AssetState => ({
  id: ids.id,
  type: AssetEntity.Unknown,
  label: ids.label ?? Object.values(ids.id).join(),
  lastUpdate: undefined,
});

export const updateFenceEvents = (
  fenceEvents: (AssetCoordinatesDwell | AssetCoordinatesTriggered)[],
) => {
  return (
    uniqBy(
      fenceEvents.map(event => {
        return {
          ...event,
          uniqueIdentifier: createGeofenceEventUniqueIdentifier(event),
        };
      }) as (AssetCoordinatesTriggered | AssetCoordinatesDwell)[],
      'uniqueIdentifier',
    ).sort((a, b) => compareDesc(new Date(a.iso8601) as Date, new Date(b.iso8601) as Date)) ?? []
  );
};

export const liveStateReducer = (
  state: LiveMapState,
  update: LiveStateOrFullUpdate & { lastStreamed?: { when: Date; event?: AssetUpdateType } },
) => {
  if (update.type === AssetUpdateType.FullStateUpdate) {
    return update.data;
  } else if (update.type === AssetUpdateType.FullStateMerge) {
    const merged: LiveMapState = {
      assets: new ExtendedMap<string, AssetState>(),
      lastUpdate:
        update.data.lastUpdate > state.lastUpdate ? update.data.lastUpdate : state.lastUpdate,
      startDate: state.startDate,
      notifications: uniqBy([...update.data.notifications, ...state.notifications], 'id').sort(
        (a, b) => a.date.getTime() - b.date.getTime(),
      ),
    };
    state.assets.forEach((asset, key) => {
      const fromUpdate: Partial<AssetState> = update.data.assets.get(key) || {};
      const assetDefinedValues: Partial<AssetState> = Object.fromEntries(
        Object.entries(asset).filter(([_, v]) => v !== null && v !== undefined),
      );
      const updatedAsset: AssetState = {
        ...fromUpdate,
        ...assetDefinedValues,
        id: asset.id,
        label: asset.label,
        lastUpdate: asset.lastUpdate ?? fromUpdate?.lastUpdate,
      };
      merged.assets.set(key, updatedAsset);
    });
    update.data.assets.forEach((assetUpdate, key) => {
      if (merged.assets.has(key)) return;
      merged.assets.set(key, assetUpdate);
    });
    return merged;
  }

  const assetId =
    update.type === AssetUpdateType.SensedTriggered ? update.data.sensedIds.id : update.data.ids.id;
  if (!assetId || assetId?.locatorId) {
    // Skipping locatorId for now, will fix in https://geomoby.atlassian.net/browse/LTP-1384
    return state;
  }
  const toUpdate = {
    ...(state.assets.get(stringifyIdRecord(assetId)) ||
      emptyAssetState(
        update.type === AssetUpdateType.SensedTriggered ? update.data.sensedIds : update.data.ids,
      )),
    ...(update.lastStreamed ? { lastStreamed: update.lastStreamed } : {}),
  };
  if (!toUpdate.type) {
    toUpdate.type = AssetEntity.Unknown;
  }
  let updatedAsset: AssetState | null = null;
  const newNotifications: Notification[] = [];

  switch (update.type) {
    case AssetUpdateType.Battery: {
      updatedAsset = {
        ...toUpdate,
        lastBattery: update.data.percent < 0 ? toUpdate.lastBattery : update.data,
      };
      break;
    }
    case AssetUpdateType.Coordinates: {
      if (toUpdate.selected) {
        const sortedRecentLocations = [update.data, ...(toUpdate.recentLocations ?? [])]
          .filter((location): location is AssetCoordinates => !!location)
          .sort((a, b) => compareDesc(new Date(a.iso8601), new Date(b.iso8601)));
        const recentLocations = capRecentLocations(sortedRecentLocations, toUpdate.id);
        updatedAsset = {
          ...toUpdate,
          recentLocations,
        };
      } else {
        updatedAsset = {
          ...toUpdate,
          recentLocations: [update.data],
        };
      }
      break;
    }
    case AssetUpdateType.CoordinatesDwell: {
      const dwellEvent = {
        ...update.data,
        isMobileEvent: true,
      };
      const lastFenceEvent = {
        ...dwellEvent,
        uniqueIdentifier: createGeofenceEventUniqueIdentifier(dwellEvent),
      };
      const fenceEvents = [lastFenceEvent, ...(toUpdate.recentFenceEvents ?? [])];
      const recentFenceEvents = capRecentFenceEvents(updateFenceEvents(fenceEvents), toUpdate.id);

      updatedAsset = {
        ...toUpdate,
        lastFenceEventDwell: lastFenceEvent,
        recentFenceEvents,
      };
      break;
    }
    case AssetUpdateType.CoordinatesTriggered: {
      const coordinatesTriggeredEvent = {
        ...update.data,
        isMobileEvent: false,
      };
      const lastFenceEvent = {
        ...coordinatesTriggeredEvent,
        uniqueIdentifier: createGeofenceEventUniqueIdentifier(coordinatesTriggeredEvent),
      };

      const fenceEvents = [lastFenceEvent, ...(toUpdate.recentFenceEvents ?? [])];
      const recentFenceEvents = capRecentFenceEvents(updateFenceEvents(fenceEvents), toUpdate.id);
      updatedAsset = {
        ...toUpdate,
        lastFenceEventBackend: lastFenceEvent,
        recentFenceEvents,
      };
      break;
    }
    case AssetUpdateType.CoordinatesTriggeredExternal: {
      const coordinatesTriggeredEventExternal = {
        ...update.data,
        isMobileEvent: true,
      };
      const lastFenceEvent = {
        ...coordinatesTriggeredEventExternal,
        uniqueIdentifier: createGeofenceEventUniqueIdentifier(coordinatesTriggeredEventExternal),
      };
      const fenceEvents = [lastFenceEvent, ...(toUpdate.recentFenceEvents ?? [])];
      const recentFenceEvents = capRecentFenceEvents(updateFenceEvents(fenceEvents), toUpdate.id);
      updatedAsset = {
        ...toUpdate,
        lastFenceEventMobile: lastFenceEvent,
        recentFenceEvents,
      };
      break;
    }
    case AssetUpdateType.Sensed: {
      const recentSensed: AssetSensed[] =
        ([update.data, toUpdate.lastSensed, ...(toUpdate.recentSensed ?? [])].filter(
          sensed =>
            !!sensed &&
            Math.abs(differenceInMinutes(new Date(update.data.iso8601), new Date(sensed.iso8601))) <
              10,
        ) as AssetSensed[]) ?? [];

      const allrecentSensed = [
        ...recentSensed,
        ...state.assets
          .toArray()
          .filter(a => !!a.recentSensed)
          .flatMap(a => a.recentSensed as AssetSensed[]),
      ].filter(s => s.sensed && s.sensed.length > 0);

      // TODO: Should only be looping through the latest https://geomoby.atlassian.net/browse/LTP-1398
      const maybeDuplicatedSensed = allrecentSensed.flatMap(a => {
        return (a.sensed ?? [])?.map(s => {
          return {
            id: s.ids.id,
            label: s.ids.label,
            stringifiedId: `${stringifyIdRecord(s.ids.id)}_${stringifyIdRecord(a.ids.id)}`,
            lastSensedBy: a.ids,
            lastUpdate: new Date(a.iso8601),
          };
        });
      });

      const sensed = uniqBy(maybeDuplicatedSensed, 'stringifiedId') as unknown as (AssetState & {
        stringifiedId: string;
      })[];

      sensed
        .sort((a, b) => compareDesc(a.lastUpdate as Date, b.lastUpdate as Date))
        .forEach(s => {
          const fromUpdate: Partial<AssetState> = state.assets.get(stringifyIdRecord(s.id)) || {};
          const updatedAsset: AssetState = {
            ...fromUpdate,
            id: s.id,
            label: s.label,
            lastUpdate: s.lastUpdate ?? fromUpdate?.lastUpdate,
            lastSensedBy: s.lastSensedBy,
            type: !fromUpdate.type ? AssetEntity.Unknown : fromUpdate.type,
          };
          state.assets.set(stringifyIdRecord(s.id), updatedAsset);
        });

      updatedAsset = {
        ...toUpdate,
        lastSensed: update.data,
        recentSensed,
        recentSensedCount: sensed.filter(
          s =>
            s.lastSensedBy && stringifyIdRecord(s.lastSensedBy.id) === stringifyIdRecord(assetId),
        ).length,
      };
      break;
    }
    case AssetUpdateType.SensedTriggered: {
      const recentSensedTriggered: AssetSensedTriggered[] =
        uniqBy(
          [
            update.data,
            toUpdate.lastSensedTriggered,
            ...(toUpdate.recentSensedTriggered ?? []),
          ].filter(
            sensedTriggered =>
              !!sensedTriggered &&
              Math.abs(
                differenceInMinutes(
                  new Date(update.data.iso8601),
                  new Date(sensedTriggered.iso8601),
                ),
              ) < 10,
          ) as AssetSensedTriggered[],
          'iso8601',
        ) ?? [];

      // TODO: Seperate lastSensedTriggered variable https://geomoby.atlassian.net/browse/LTP-1398
      updatedAsset = {
        ...toUpdate,
        lastSensedTriggered: update.data,
        recentSensedTriggered,
      };
      break;
    }
    case AssetUpdateType.SensorTriggeredBoundary: {
      updatedAsset = {
        ...toUpdate,
        lastSensedTriggeredBoundary: update.data,
      };
      break;
    }
    case AssetUpdateType.Temperature: {
      updatedAsset = {
        ...toUpdate,
        temperature: update.data,
      };
      break;
    }
    default:
      return state;
  }

  if (!updatedAsset) return state;

  const updateDate = new Date(update.data.iso8601);
  if (!updatedAsset.lastUpdate) {
    updatedAsset.lastUpdate = updateDate;
  } else {
    updatedAsset.lastUpdate =
      updateDate.getTime() > updatedAsset.lastUpdate.getTime()
        ? updateDate
        : updatedAsset.lastUpdate;
  }

  // Previously: two `.filter(...)` blocks here for `recentFenceEvents` and
  // `recentLocations` whose return values were never assigned (no-ops). They
  // were intended as a 10-minute sliding window but never worked. Bounding is
  // now handled by the explicit `MAX_RECENT_FENCE_EVENTS` / `MAX_RECENT_LOCATIONS`
  // caps applied inside the relevant `case` branches above.

  if (
    update.type === AssetUpdateType.SensedTriggered ||
    update.type === AssetUpdateType.CoordinatesTriggered
  ) {
    newNotifications.push({
      id: `${update.type}_${update.data.iso8601}_${stringifyIdRecord(updatedAsset.id)}`,
      date: new Date(update.data.iso8601),
      seen: false,
      asset: updatedAsset,
      updateType: update.type,
    });
  }

  const assets = state.assets;
  if (updatedAsset !== null) {
    let type = updatedAsset.type ?? AssetEntity.Unknown;
    if (!updatedAsset.type || updatedAsset.type === AssetEntity.Unknown) {
      type = assetTypeOfEntity(updatedAsset.id);
    }
    state.assets.set(stringifyIdRecord(assetId), {
      ...updatedAsset,
      type,
    });
  }

  /*
    Currently the back-end doesn't provide the list of sensed on the initial Persistor call to prevent overwealming the data. 
    Until find some other way around this on the back-end, we need to resort to this forEach loop. This forEach Loop should really be in the FullStateUpdate and FullStateMerge
    conditions at the top of the function but Replay's inital call to Persitor doesn't do the same as the call in Live Map, so we would still not have the Sensed list filled on start-up.
  */
  assets.forEach(asset => {
    if (asset.lastSensed && (!asset.lastSensed.sensed || asset.lastSensed.sensed.length === 0)) {
      const sensed = assets
        .toArray()
        .filter(
          a =>
            a.recentLocations?.[0]?.sourceIds?.id &&
            stringifyIdRecord(a.recentLocations?.[0]?.sourceIds?.id) ===
              stringifyIdRecord(asset.id),
        )
        ?.map(s => {
          return {
            ids: {
              id: s.id,
              label: s.label,
            } as ProjectlessAssetIdentifiers,
          };
        });
      asset.lastSensed.sensed = sensed;
      asset.recentSensedCount = sensed.length;
    }
  });

  // Another possibly temporary addition. Recipients of SensedTriggered aren't always updated when there sensor counterpart is. That's why we do it here.
  if (toUpdate.lastSensedTriggered) {
    const lastSensedTriggered = toUpdate.lastSensedTriggered;
    const sensedTriggeredAsset = assets.get(stringifyIdRecord(lastSensedTriggered.sensedIds.id));
    if (sensedTriggeredAsset) {
      assets.set(stringifyIdRecord(sensedTriggeredAsset.id), {
        ...sensedTriggeredAsset,
        lastSensedTriggered: lastSensedTriggered,
        recentSensedTriggered: toUpdate.recentSensedTriggered,
      });
    }
  }

  return {
    ...state,
    assets,
    lastUpdate: updateDate.getTime() > state.lastUpdate.getTime() ? updateDate : state.lastUpdate,
    notifications: uniqBy([...state.notifications, ...newNotifications], 'id').sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    ),
  };
};

export const useGeomobyLiveStream = (
  lastEventsFromPersistorAtom: PrimitiveAtom<LiveStateUpdate[]>,
  liveUpdatesLatestsAtom: Atom<LiveStateUpdate[]>,
): StreamData => {
  const startDate = useMemo(() => new Date(), []);
  const [liveState, liveDispatch] = useReducer(liveStateReducer, INITIAL_STATE);
  const lastLMU = useRef<LiveStateUpdate[]>([]);
  const lastStreamed = useRef<Date | undefined>(undefined);
  const lastEventsFromPersistor = useAtomValue(lastEventsFromPersistorAtom);
  const latestEvents = useAtomValue(liveUpdatesLatestsAtom);
  const setNotifications = useSetAtom(LIVE_NOTIFICATIONS);
  const cid = useAtomValue(CID);
  const pid = useAtomValue(PID);

  // Sets the initial state from the initial batch of events.
  // "Full state merge" does not clear data from livestream message that
  // arrive before the last events from persistor have been fetched.
  useEffect(() => {
    if (lastEventsFromPersistor && lastEventsFromPersistor.length > 0) {
      const msgs: LiveStateUpdate[] = lastEventsFromPersistor;
      const actualInitialState: LiveMapState = msgs.reduce(
        (state: LiveMapState, msg: LiveStateUpdate) => liveStateReducer(state, msg),
        INITIAL_STATE,
      );
      const updateEvent: LiveStateOrFullUpdate = {
        type: AssetUpdateType.FullStateMerge,
        data: actualInitialState,
      };
      liveDispatch(updateEvent);
      lastLMU.current = [...lastLMU.current, ...msgs];
    }
  }, [lastEventsFromPersistor]);

  // Clear INITIAL_STATE when client/project changes.
  useEffect(() => {
    INITIAL_STATE.assets.clear();
    INITIAL_STATE.lastUpdate = new Date();
    INITIAL_STATE.startDate = new Date();
    INITIAL_STATE.notifications = [];
  }, [cid, pid]);

  useEffect(() => {
    setNotifications(liveState.notifications);
  }, [liveState, setNotifications]);

  // Dispatches live events as they come in
  useEffect(() => {
    if (latestEvents && latestEvents.length) {
      lastStreamed.current = new Date();
      latestEvents.forEach(event => {
        const alreadyDispatched = lastLMU.current.find(
          p => JSON.stringify(p) === JSON.stringify(event),
        );
        if (!alreadyDispatched) {
          liveDispatch({
            ...event,
            lastStreamed: lastStreamed.current
              ? { when: lastStreamed.current, event: event.type }
              : undefined,
          });
        }
      });
      lastLMU.current = latestEvents;
    }
  }, [latestEvents]);

  const result = {
    state: {
      ...liveState,
      ...(lastStreamed.current
        ? { lastStreamed: { when: lastStreamed.current, event: liveState.lastStreamed?.event } }
        : {}),
    },
    startDate,
  };
  lastStreamed.current = undefined;
  return result;
};
